// /app/frontend/src/pages/compliance/maturity/MaturityDomainPage.jsx
/**
 * TR2UST v2.0 - Domain Controls View
 * Swiss & High-Contrast Design with Dark Mode Support
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { maturityAPI } from '../../../services/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import {
  ArrowLeft, ChevronRight, Shield, Home, Scale, Users, Database,
  Lock, Briefcase, Bell, CheckCircle, AlertCircle, MinusCircle
} from 'lucide-react';
import { TIER_BANNER, MATURITY_LEVELS_MAP } from './maturityConfig';

// Domain config
const DOMAIN_CONFIG = {
  GOV: { icon: Scale, color: '#3B82F6', name: 'Governance & Accountability' },
  RCT: { icon: Users, color: '#8B5CF6', name: 'Rights, Consent & Transparency' },
  DLM: { icon: Database, color: '#06B6D4', name: 'Data Lifecycle Management' },
  SEC: { icon: Lock, color: '#10B981', name: 'Information Security' },
  TPM: { icon: Briefcase, color: '#F59E0B', name: 'Third-Party Management' },
  INC: { icon: Bell, color: '#EF4444', name: 'Incident & Breach Management' }
};

// Tier config
const TIER_CONFIG = {
  T1: { label: 'T1', bg: 'bg-emerald-500/10 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-400', desc: 'Foundational' },
  T2: { label: 'T2', bg: 'bg-blue-500/10 dark:bg-blue-500/20', text: 'text-blue-700 dark:text-blue-400', desc: 'Standard' },
  T3: { label: 'T3', bg: 'bg-violet-500/10 dark:bg-violet-500/20', text: 'text-violet-700 dark:text-violet-400', desc: 'Advanced' }
};

// Control Card Component
function ControlCard({ control, assessment, onClick, domainColor }) {
  const tier = TIER_CONFIG[control.tier] || TIER_CONFIG.T1;
  const isAssessed = assessment && !assessment.is_na && assessment.maturity_level;
  const isNA = assessment?.is_na;
  
  const getStatus = () => {
    if (isNA) return { label: 'N/A', bg: 'bg-slate-100 dark:bg-slate-700', text: 'text-slate-500 dark:text-slate-400', icon: MinusCircle };
    if (isAssessed) {
      const level = MATURITY_LEVELS_MAP[assessment.maturity_level];
      return { label: `Level ${assessment.maturity_level}`, bg: level.bg, text: level.text, icon: CheckCircle };
    }
    return { label: 'Not Assessed', bg: 'bg-red-500/10 dark:bg-red-500/20', text: 'text-red-600 dark:text-red-400', icon: AlertCircle };
  };
  
  const status = getStatus();
  const StatusIcon = status.icon;

  return (
    <Card 
      onClick={onClick}
      className="group bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-blue-300/50 dark:hover:border-blue-500/50 transition-all duration-200 cursor-pointer overflow-hidden"
      data-testid={`control-card-${control.control_id}`}
    >
      <CardContent className="p-0">
        {/* Top colored accent bar */}
        <div className="h-1" style={{ backgroundColor: domainColor }} />
        
        <div className="p-4">
          {/* Header row */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {/* Control ID */}
              <span 
                className="inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-bold text-white"
                style={{ backgroundColor: domainColor, fontFamily: 'JetBrains Mono, monospace' }}
              >
                {control.control_id}
              </span>
              {/* Tier badge */}
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${tier.bg} ${tier.text}`}>
                {tier.label}
              </span>
            </div>
            {/* Weight */}
            <span className="text-sm font-semibold text-slate-900 dark:text-white">{control.control_weight}%</span>
          </div>
          
          {/* Title */}
          <h3 className="font-medium text-slate-900 dark:text-white text-sm leading-snug mb-3 line-clamp-2 min-h-[40px]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {control.title}
          </h3>
          
          {/* Status badge */}
          <div className="flex items-center justify-between">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.text}`}>
              <StatusIcon className="h-3.5 w-3.5" strokeWidth={2} />
              {status.label}
            </span>
            <ChevronRight className="h-4 w-4 text-slate-300 dark:text-slate-600 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Progress Bar Component
function DomainProgressBar({ score, level, domainColor }) {
  const levelConfig = MATURITY_LEVELS_MAP[level] || MATURITY_LEVELS_MAP[1];
  
  return (
    <div className="flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Domain Score</span>
          <span className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {score}%
          </span>
        </div>
        <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${score}%`, backgroundColor: domainColor }}
          />
        </div>
      </div>
      <div className="pl-4 border-l border-slate-200 dark:border-slate-700">
        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${levelConfig.bg} ${levelConfig.text}`}>
          {levelConfig.name}
        </span>
      </div>
    </div>
  );
}

export default function MaturityDomainPage() {
  const { domainCode } = useParams();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get('entity');
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState(null);
  const [controls, setControls] = useState([]);
  const [assessments, setAssessments] = useState({});
  const [score, setScore] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (!domainCode || !entityId) return;

      setLoading(true);
      try {
        const [domainsRes, controlsRes, assessmentsRes, scoreRes, tierRes] = await Promise.all([
          maturityAPI.getDomains(),
          maturityAPI.getDomainControls(domainCode, entityId),  // Pass entityId for tier filtering
          maturityAPI.getAssessments(entityId, domainCode),
          maturityAPI.getScore(entityId).catch(() => ({ data: null })),
          maturityAPI.getTier(entityId).catch(() => ({ data: null }))
        ]);

        const domainData = domainsRes.data.find(d => d.code === domainCode.toUpperCase());
        setDomain(domainData);
        setControls(controlsRes.data);
        setScore(scoreRes.data);
        setTierInfo(tierRes.data);

        const lookup = {};
        assessmentsRes.data.forEach(a => { lookup[a.control_id] = a; });
        setAssessments(lookup);
      } catch (error) {
        toast.error('Failed to load domain data');
      } finally {
        setLoading(false);
      }
    };
    loadData();

    // Check for snapshot prompt. Fire inline — an earlier setTimeout-based
    // implementation was getting cancelled by React StrictMode's double-
    // invocation cleanup (first setup sets the flag + schedules timer, the
    // strict-mode cleanup clears the timer, and the re-run sees the flag as
    // already set and skips). Toaster is globally mounted so firing during
    // effect setup is safe.
    const hasAssessed = sessionStorage.getItem('maturityAssessedInSession') === 'true';
    const alreadyPrompted = sessionStorage.getItem('snapshotPromptShown') === 'true';
    if (hasAssessed && !alreadyPrompted && entityId) {
      sessionStorage.setItem('snapshotPromptShown', 'true');
      showSnapshotPrompt();
    }
  }, [domainCode, entityId]);

  const showSnapshotPrompt = () => {
    toast('Assessment session complete', {
      description: 'Save a snapshot to record your current score and track progress over time.',
      duration: Infinity,
      action: { label: 'Save Snapshot', onClick: handleSaveSnapshot },
      cancel: { label: 'Not Now', onClick: () => {} }
    });
  };

  const handleSaveSnapshot = async () => {
    try {
      await maturityAPI.createSnapshot(entityId);
      toast.success('Snapshot saved successfully');
    } catch (error) {
      toast.error('Failed to save snapshot');
    }
  };

  const handleViewControl = (controlId) => {
    navigate(`/compliance/maturity/control/${controlId}?entity=${entityId}`);
  };

  const config = DOMAIN_CONFIG[domainCode?.toUpperCase()] || { icon: Shield, color: '#64748B', name: domainCode };
  const Icon = config.icon;
  const domainScore = score?.domain_scores?.[domainCode?.toUpperCase()];
  const currentScore = domainScore?.score || 0;
  const currentLevel = domainScore?.maturity_level || 1;
  
  const assessedCount = Object.values(assessments).filter(a => !a.is_na && a.maturity_level).length;
  const naCount = Object.values(assessments).filter(a => a.is_na).length;
  const remaining = controls.length - assessedCount - naCount;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pageContent = (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Link to="/dashboard" className="hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1 transition-colors">
          <Home className="h-4 w-4" strokeWidth={1.5} />
          <span>Dashboard</span>
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to="/compliance/maturity" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
          Maturity Assessment
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-slate-900 dark:text-white font-medium">{domain?.code}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/compliance/maturity')}
            className="mt-1 hover:bg-slate-100 dark:hover:bg-slate-800"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={1.5} />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${config.color}20` }}
              >
                <Icon className="h-6 w-6" style={{ color: config.color }} strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {domain?.code}: {config.name}
                </h1>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {controls.length} controls • {Math.round((domain?.weight || 0) * 100)}% of total weight
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Stats Pills */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 rounded-full text-sm font-medium">
            <CheckCircle className="h-4 w-4" strokeWidth={1.5} />
            {assessedCount} assessed
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded-full text-sm font-medium">
            <AlertCircle className="h-4 w-4" strokeWidth={1.5} />
            {remaining} pending
          </div>
          {naCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-full text-sm font-medium">
              <MinusCircle className="h-4 w-4" strokeWidth={1.5} />
              {naCount} N/A
            </div>
          )}
        </div>
      </div>

      {/* Tier Context Banner (Change 1) */}
      {tierInfo && (
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-600 dark:text-slate-400">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
            {tierInfo.maturity_tier}
          </span>
          <span>{TIER_BANNER[tierInfo.maturity_tier]}</span>
        </div>
      )}

      {/* Progress Bar */}
      <DomainProgressBar score={currentScore} level={currentLevel} domainColor={config.color} />

      {/* Controls Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {controls.map(control => (
          <ControlCard
            key={control.control_id}
            control={control}
            assessment={assessments[control.control_id]}
            onClick={() => handleViewControl(control.control_id)}
            domainColor={config.color}
          />
        ))}
      </div>
    </div>
  );
  
  return (
    <DashboardLayout>
      {pageContent}
    </DashboardLayout>
  );
}
